// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import usePageSize from "@/hooks/usePageSize";
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import type { TyOrganization } from "@/types";
import { catalogUrl, readLangValue } from "@/utils";
import OrganizationCard from "@/UI/cards/OrganizationCard";
import ActionsBar, { type TyBarTab } from "@/UI/components/ActionsBar";
import ListPanel from "@/UI/components/ListPanel";
import Paginator from "@/UI/components/Paginator";
import FilterSearchInput from "@/UI/inputs/FilterSearchInput";
import ActionsPopup, { type TyAction } from "@/UI/popups/ActionsPopup";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import OrganizationFormPopup from "@/UI/popups/OrganizationFormPopup";
// --- Logic --------------------------------------------------------------------------------------
type TyTab = "all" | "blocked";

/** What each scope asks the endpoint for. A tab is a different list, not a narrowing of one. */
const TAB_QUERY: Record<TyTab, Record<string, string>> = {
    all: {},
    blocked: { isBlocked: "true" },
};

/**
 * Every developer company on Lozicore, and the way into all of them.
 *
 * The tab, the search and the page live in the URL, so a filtered list is a link somebody can send.
 * They **replace** rather than push: narrowing a list is an action on what is already on screen, not a
 * place, and pushing would make the back button undo a filter instead of leaving the section.
 *
 * Only stored fields are searched — a name, a slug, a city, whether it is blocked. Whether a company
 * pays is derived per page and shown on its card, not filtered by; see `functions/organizationStats`.
 */
export default function OrganizationsScreen() {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";
    const navigate = useNavigate();
    const addComp = usePopup((state) => state.addComp);
    const [params, setParams] = useSearchParams();
    const pageSize = usePageSize((state) => state.pageSize);

    const tab = (params.get("tab") as TyTab) || "all";
    const search = params.get("search") ?? "";
    const page = Math.max(1, Number(params.get("page")) || 1);

    const [isFiltering, setIsFiltering] = useState(false);
    const [draftSearch, setDraftSearch] = useState(search);

    const organizations = useFetch<{ organizations: TyOrganization[]; total: number }>("/organizations", {}, false);
    /** Row actions share one call: only one can be in flight, and each sets its own path. */
    const action = useFetch("/organizations", {}, false);

    const load = () => {
        organizations.invoke({ query: { ...TAB_QUERY[tab], search: search.trim() || undefined, page, limit: pageSize } });
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, search, page, pageSize]);

    useEffect(() => {
        if (!action.succeededAt) return;
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action.succeededAt]);

    const apply = (next: Record<string, string | null>) => {
        const merged = new URLSearchParams(params);
        for (const [key, value] of Object.entries(next)) {
            if (value) merged.set(key, value);
            else merged.delete(key);
        }
        // Narrowing a list — or changing tab — puts you back at its start.
        if (!("page" in next)) merged.delete("page");
        setParams(merged, { replace: true });
    };

    const rows = organizations.data?.organizations ?? [];
    const tabs: TyBarTab[] = (Object.keys(TAB_QUERY) as TyTab[]).map((key) => ({ key, label: t.organizations.tabs[key] }));

    const openForm = (organization: TyOrganization | null) =>
        addComp({
            name: "organization-form",
            Component: OrganizationFormPopup,
            place: "end",
            anim: "endSlide",
            details: {
                organization,
                /**
                 * A new company is opened straight away, **on its team tab**: creating one is the
                 * start of a job, not the end, and the next step is the owner — until it has one,
                 * nobody can sign into the company that was just sold.
                 */
                onDone: (saved: TyOrganization) => (organization ? load() : navigate(`/organizations/${saved.slug}?tab=people`)),
            },
        });

    const openActions = (organization: TyOrganization, anchor: DOMRect) => {
        const name = readLangValue(organization.name, lang) || organization.slug;

        // No "open" entry: the whole card already does that, and offering it again took the first
        // row of every menu to repeat the one thing the card is for.
        const actions: TyAction[] = [
            { key: "edit", label: t.common.edit, icon: "edit", onSelect: () => openForm(organization) },
            {
                key: "catalog",
                label: t.organizations.openCatalog,
                icon: "link",
                onSelect: () => window.open(catalogUrl(organization.slug), "_blank"),
            },
            {
                key: "block",
                label: organization.isBlocked ? t.organizations.unblock : t.organizations.block,
                icon: "block",
                danger: !organization.isBlocked,
                onSelect: () =>
                    addComp({
                        name: "confirm",
                        Component: ConfirmPopup,
                        anim: "scale",
                        details: {
                            title: organization.isBlocked ? t.organizations.unblock : t.organizations.block,
                            message: (organization.isBlocked ? t.organizations.confirmUnblock : t.organizations.confirmBlock).replace("{name}", name),
                            confirmLabel: organization.isBlocked ? t.organizations.unblock : t.organizations.block,
                            danger: !organization.isBlocked,
                            onConfirm: () =>
                                action.invoke({
                                    method: "PATCH",
                                    path: `/organizations/${organization._id}`,
                                    data: { isBlocked: !organization.isBlocked },
                                }),
                        },
                    }),
            },
        ];

        addComp({
            name: "organization-actions",
            Component: ActionsPopup,
            place: "anchor-below",
            anim: "scaleTop",
            cover: "invisible",
            details: { actions },
            anchor: { top: anchor.top, left: anchor.left, right: anchor.right, bottom: anchor.bottom, width: anchor.width, height: anchor.height },
        });
    };

    return (
        <section className="list-screen">
            <ListPanel title={t.organizations.title} subtitle={t.organizations.subtitle.replace("{count}", String(organizations.data?.total ?? 0))}>
                <ActionsBar
                    tabs={tabs}
                    activeTab={tab}
                    onTab={(key) => apply({ tab: key === "all" ? null : key })}
                    addLabel={t.organizations.addOrganization}
                    onAdd={() => openForm(null)}
                    isFilterOpen={isFiltering}
                    onFilterOpen={() => {
                        setDraftSearch(search);
                        setIsFiltering(true);
                    }}
                    hasFilters={!!search}
                    onFilterApply={() => {
                        setIsFiltering(false);
                        apply({ search: draftSearch.trim() || null });
                    }}
                    onFilterClear={() => {
                        setDraftSearch("");
                        setIsFiltering(false);
                        apply({ search: null });
                    }}
                    onFilterCancel={() => setIsFiltering(false)}
                    filterContent={
                        <FilterSearchInput
                            value={draftSearch}
                            onChange={setDraftSearch}
                            label={t.common.search}
                            placeholder={t.organizations.searchPlaceholder}
                            onEnter={() => {
                                setIsFiltering(false);
                                apply({ search: draftSearch.trim() || null });
                            }}
                        />
                    }
                />
            </ListPanel>

            {action.error && <p className="screen-state screen-state--error">{action.error}</p>}
            {organizations.loading && <p className="screen-state">{t.common.loading}</p>}
            {organizations.error && <p className="screen-state screen-state--error">{organizations.error}</p>}
            {!organizations.loading && !organizations.error && !rows.length && <p className="screen-state">{t.organizations.empty}</p>}

            <div className="list-screen__grid">
                {rows.map((organization) => (
                    <OrganizationCard
                        key={organization._id}
                        organization={organization}
                        onOpen={() => navigate(`/organizations/${organization.slug}`)}
                        onActions={(anchor) => openActions(organization, anchor)}
                    />
                ))}
            </div>

            <Paginator
                page={page}
                total={organizations.data?.total ?? 0}
                count={rows.length}
                onPage={(next) => apply({ page: next === 1 ? null : String(next) })}
            />
        </section>
    );
}
